'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _defineProperty = _interopDefault(require('@babel/runtime/helpers/defineProperty'));
var React = require('react');
var React__default = _interopDefault(React);
var PropTypes = require('prop-types');
var PropTypes__default = _interopDefault(PropTypes);
var nthGrid = require('@narvar/nth-grid');
var polished = require('polished');
var layoutTree = require('./layoutTree.js');
var _slicedToArray = _interopDefault(require('@babel/runtime/helpers/slicedToArray'));
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var useWindowSize = require('./hooks/useWindowSize.js');
var useRem = require('./hooks/useRem.js');
var renderLayout = require('./renderLayout.js');
var gridComponents = require('./gridComponents.js');
var WaterfallOrchestrator = require('./WaterfallOrchestrator.js');

var _PropTypes$exact;
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var defaultViewRenderProp = function defaultViewRenderProp(options) {
  return /*#__PURE__*/React__default.createElement(gridComponents.containerComponent, null, options && options.children);
};
var defaultRowRenderProp = function defaultRowRenderProp(options) {
  return /*#__PURE__*/React__default.createElement(gridComponents.rowComponent, null, options && options.children);
};
var defaultColRenderProp = function defaultColRenderProp(options) {
  return /*#__PURE__*/React__default.createElement(gridComponents.columnComponent, {
    "xs-columns": options && options.node && options.node.width || 12
  }, options && options.children);
};
var defaultStackRenderProp = function defaultStackRenderProp(options) {
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, options && /*#__PURE__*/React__default.createElement(WaterfallOrchestrator.default, {
    feature: options.feature
  }, options.children));
};
var evaluateCondition = function evaluateCondition(conditionOperand, operator, value) {
  switch (operator) {
    case layoutTree.ConditionOperator.NEQ:
      return conditionOperand !== value;
    case layoutTree.ConditionOperator.IN:
      if (Array.isArray(conditionOperand)) {
        return conditionOperand.includes(value);
      }
      // eslint-disable-next-line no-console
      console.error("Array operand was not specified for IN operator: ".concat(JSON.stringify({
        conditionOperand: conditionOperand,
        operator: operator,
        value: value
      })));
      return false;
    case layoutTree.ConditionOperator.EQ:
    default:
      return conditionOperand === value;
  }
};
var filterLayoutForConditions = function filterLayoutForConditions(node, status, carrier, category) {
  var newNode = _objectSpread({}, node);
  if (newNode.type === layoutTree.TreeNodeTypes.STACK && newNode.children) {
    // Only return components that are relevant for the current layout conditions (status and carrier)
    var feature = newNode.children.find(function (feature) {
      var _feature$options, _feature$options$cond;
      return feature === null || feature === void 0 ? void 0 : (_feature$options = feature.options) === null || _feature$options === void 0 ? void 0 : (_feature$options$cond = _feature$options.conditions) === null || _feature$options$cond === void 0 ? void 0 : _feature$options$cond.every(function (condition) {
        if (condition.type === layoutTree.ConditionType.SHIPMENT_STATUS) {
          return evaluateCondition(condition.status, condition.operator, status);
        }
        if (condition.type === layoutTree.ConditionType.CARRIER_MONIKER) {
          return evaluateCondition(condition.carrierMoniker, condition.operator, carrier);
        }
        if (condition.type === layoutTree.ConditionType.CATEGORY) {
          return evaluateCondition(condition.category, condition.operator, category);
        }
        return true;
      });
    });
    newNode.children = feature ? [feature] : [];
  } else if (newNode.type === layoutTree.TreeNodeTypes.ROW) {
    newNode.children = newNode.children.map(function (child) {
      return filterLayoutForConditions(child, status, carrier, category);
    });
    return newNode;
  } else if (newNode.type === layoutTree.TreeNodeTypes.COL) {
    newNode.children = newNode.children.map(function (child) {
      return filterLayoutForConditions(child, status, carrier, category);
    });
  } else if (newNode.type === layoutTree.TreeNodeTypes.VIEW) {
    newNode.children = newNode.children.map(function (child) {
      return filterLayoutForConditions(child, status, carrier, category);
    });
  }
  return newNode;
};
var LayoutComponent = function LayoutComponent(_ref) {
  var deviceLayout = _ref.deviceLayout,
    userAgentDevice = _ref.userAgentDevice,
    breakpoints = _ref.breakpoints,
    view = _ref.view,
    row = _ref.row,
    col = _ref.col,
    stack = _ref.stack,
    feature = _ref.feature,
    fixedSize = _ref.fixedSize;
  var _usePageContext = nthHookContextPage.usePageContext(),
    _usePageContext$state = _usePageContext.state,
    carrier = _usePageContext$state.carrier,
    trackingStatus = _usePageContext$state.trackingStatus,
    category = _usePageContext$state.category;
  var windowWidth = (useWindowSize.default({
    fixedSize: fixedSize
  }).width || 0) / useRem.default();
  var possibleDevicePerWindowWidth = function possibleDevicePerWindowWidth() {
    if (!windowWidth || !breakpoints) return undefined;
    return windowWidth >= breakpoints[layoutTree.Devices.DESKTOP] ? layoutTree.Devices.DESKTOP : layoutTree.Devices.MOBILE;
  };

  // The Devices fallback order here is purposefully distinct between first render and
  // useEffect updates. LayoutComponent can be SSR'd, which means it always needs to
  // have its initial layout state first be determined by userAgentDevice. Otherwise,
  // if we prefer window width first, the initial state in the render on the server
  // can be different than the initial state in the browser, leading to initial hydration problems.
  // When we're performing subsequent updates in the browser (aka, updating from
  // the useEffect hook), we want to prefer the window width.
  var getDeviceTypeForFirstRender = function getDeviceTypeForFirstRender() {
    return userAgentDevice || possibleDevicePerWindowWidth() || layoutTree.Devices.MOBILE;
  };
  var getDeviceTypeForUseEffectUpdates = function getDeviceTypeForUseEffectUpdates() {
    return possibleDevicePerWindowWidth() || userAgentDevice || layoutTree.Devices.MOBILE;
  };
  var _useState = React.useState(deviceLayout[getDeviceTypeForFirstRender()]),
    _useState2 = _slicedToArray(_useState, 2),
    layout = _useState2[0],
    setLayout = _useState2[1];

  // we initially want to set layout based on known device
  // type or windowWidth (if already defined, as per embeds),
  // but also allow for updating this on screen resize.
  React.useEffect(function () {
    setLayout(deviceLayout[getDeviceTypeForUseEffectUpdates()]);
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [windowWidth]);
  if (!layout) return null;
  var filteredLayout = filterLayoutForConditions(layout, trackingStatus || layoutTree.DEFAULT_NARVAR_STATUS, carrier || '', category || '');
  return renderLayout.default({
    node: filteredLayout,
    view: view || defaultViewRenderProp,
    row: row || defaultRowRenderProp,
    col: col || defaultColRenderProp,
    stack: stack || defaultStackRenderProp,
    feature: feature
  });
};
LayoutComponent.defaultProps = {
  breakpoints: _defineProperty({}, layoutTree.Devices.DESKTOP, polished.stripUnit(nthGrid.constants.breakpointMinWidths[nthGrid.constants.WIDTH_MD])),
  view: defaultViewRenderProp,
  row: defaultRowRenderProp,
  col: defaultColRenderProp
};
LayoutComponent.propTypes = {
  breakpoints: PropTypes__default.shape(_defineProperty({}, layoutTree.Devices.DESKTOP, PropTypes__default.number.isRequired)),
  view: PropTypes__default.func,
  row: PropTypes__default.func,
  col: PropTypes__default.func,
  stack: PropTypes__default.func,
  feature: PropTypes__default.func.isRequired,
  userAgentDevice: PropTypes__default.oneOf([layoutTree.Devices.MOBILE, layoutTree.Devices.DESKTOP]).isRequired,
  // Casting types breaks the check for default props, so ignore it

  deviceLayout: PropTypes__default.exact((_PropTypes$exact = {}, _defineProperty(_PropTypes$exact, layoutTree.Devices.MOBILE, PropTypes__default.exact({
    id: PropTypes__default.string.isRequired,
    children: PropTypes__default.arrayOf(PropTypes__default.object).isRequired,
    type: PropTypes__default.oneOf([layoutTree.TreeNodeTypes.VIEW]).isRequired
  }).isRequired), _defineProperty(_PropTypes$exact, layoutTree.Devices.DESKTOP, PropTypes__default.exact({
    id: PropTypes__default.string.isRequired,
    children: PropTypes__default.arrayOf(PropTypes__default.object).isRequired,
    type: PropTypes__default.oneOf([layoutTree.TreeNodeTypes.VIEW]).isRequired
  }).isRequired), _PropTypes$exact)).isRequired
};

exports.default = LayoutComponent;
